import { useLayoutEffect, useRef, useState, type ReactNode } from "react"

/** 카드 사이 간격(px). 행 높이 계산과 렌더링이 같은 값을 쓴다. */
const GAP = 16
/** 목표 행 높이 = 컨테이너 폭 / 4 를 이 범위로 제한 (1024px 폭에서 정사각 카드 약 4장, 모바일 2장) */
const MIN_ROW_HEIGHT = 160
const MAX_ROW_HEIGHT = 260
/** 마지막 카드를 다음 행으로 넘길 때 허용하는 최대 행 높이 (목표 높이 대비 배수) */
const MAX_ROW_STRETCH = 1.5

interface Row {
  start: number
  /** exclusive */
  end: number
  /** false 면 마지막 미완성 행: 늘리지 않고 목표 높이로 왼쪽 정렬 */
  justified: boolean
}

/** "16:9" → 16/9. 형식이 맞지 않으면 1 (정사각) */
function parseAspectRatio(value: string | null | undefined): number {
  const [w, h] = (value ?? "").split(":").map(Number)
  return w > 0 && h > 0 ? w / h : 1
}

/**
 * 카드 비율(폭/높이) 목록을 행으로 나눈다.
 * 행 높이가 목표 이하로 내려올 때까지(= 폭이 다 찰 때까지) 카드를 담고, 마지막 카드를
 * 넣은 행과 다음 행으로 넘긴 행 중 목표 높이에 더 가까운 쪽을 고른다.
 */
function layoutRows(ratios: number[], width: number, targetHeight: number): Row[] {
  const heightOf = (ratioSum: number, count: number) => (width - GAP * (count - 1)) / ratioSum
  const rows: Row[] = []
  let start = 0

  while (start < ratios.length) {
    let ratioSum = 0
    let last = start
    for (; last < ratios.length; last++) {
      ratioSum += ratios[last]
      if (heightOf(ratioSum, last - start + 1) <= targetHeight) break
    }

    if (last === ratios.length) {
      rows.push({ start, end: last, justified: false })
      break
    }

    const withLast = heightOf(ratioSum, last - start + 1)
    const withoutLast =
      last > start ? heightOf(ratioSum - ratios[last], last - start) : Infinity
    const dropLast =
      withoutLast - targetHeight < targetHeight - withLast &&
      withoutLast <= targetHeight * MAX_ROW_STRETCH
    const end = dropLast ? last : last + 1
    rows.push({ start, end, justified: true })
    start = end
  }

  return rows
}

interface JustifiedRowsProps<T> {
  items: T[]
  getKey: (item: T) => string
  /** 카드 이미지의 비율 ("16:9" 형식) */
  getAspectRatio: (item: T) => string | null | undefined
  renderItem: (item: T) => ReactNode
}

/**
 * 행 맞춤(justified) 갤러리 레이아웃.
 *
 * 카드는 좌→우로 채워지고 다음 행으로 내려간다. 한 행의 카드는 같은 높이로 맞춰지고
 * 폭은 이미지 비율대로 나뉘어 이미지를 자르지 않는다. 렌더된 카드의 이미지 영역은
 * 비율(aspect-ratio)을 유지하고 가로 테두리가 없어야 행 높이가 정확히 맞는다.
 * 각 카드 칸은 행 높이로 늘어나므로, 아래 텍스트 길이가 달라도 h-full 카드의 바닥이 맞는다.
 * 무한스크롤로 항목을 끝에 append하면 마지막 행부터만 다시 배치된다.
 */
export function JustifiedRows<T>({ items, getKey, getAspectRatio, renderItem }: JustifiedRowsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  // 컨테이너 폭(윈도우·사이드바 변화)을 따라간다. ResizeObserver 의 첫 통지는 레이아웃 후
  // 페인트 전에 오므로 폭을 모르는 빈 상태가 화면에 그려지지 않는다.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const ratios = items.map((item) => parseAspectRatio(getAspectRatio(item)))
  const targetHeight = Math.min(MAX_ROW_HEIGHT, Math.max(MIN_ROW_HEIGHT, width / 4))
  const rows = width > 0 ? layoutRows(ratios, width, targetHeight) : []

  return (
    <div ref={containerRef} className="flex flex-col" style={{ gap: GAP }}>
      {rows.map((row) => (
        <div key={row.start} className="flex" style={{ gap: GAP }}>
          {items.slice(row.start, row.end).map((item, offset) => {
            const ratio = ratios[row.start + offset]
            return (
              <div
                key={getKey(item)}
                className="min-w-0"
                style={
                  row.justified
                    ? { flex: `${ratio} ${ratio} 0%` }
                    : { flex: "none", width: ratio * targetHeight }
                }
              >
                {renderItem(item)}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
